/**
 * Shared, universal referral helpers — safe to import on client or server.
 * Covers the referral cookie name/TTL, code validation, and link building for
 * the generic user-to-user referral system.
 */

/** Query param carried on shareable referral links, e.g. `/?ref=uP63-GTl`. */
export const REFERRAL_QUERY_PARAM = "ref";

/** First-party cookie that persists a captured referral code until signup. */
export const REF_CODE_COOKIE_NAME = "refCode";

/** Persist a captured referral code for 90 days. */
export const REF_CODE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

/** Public site origin used to build shareable referral links. */
export const REFERRAL_SITE_ORIGIN = "https://dreamcoachmatch.com";

// Referral codes are nanoid(8) values (alphabet: A-Za-z0-9_-). We accept a
// slightly wider, bounded shape to stay tolerant of older/coach codes while
// rejecting anything that clearly isn't a code (querystrings, scripts, etc.).
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;

export const isValidReferralCode = (value: unknown): value is string =>
  typeof value === "string" && REFERRAL_CODE_PATTERN.test(value);

/**
 * Parse a referral code read from a cookie/header value. Cookie values may be
 * URL-encoded; returns the decoded code if valid, otherwise null.
 */
export const parseReferralCodeCookie = (
  cookieValue: string | undefined | null,
): string | null => {
  if (!cookieValue) {
    return null;
  }

  let decoded = cookieValue;
  try {
    decoded = decodeURIComponent(cookieValue);
  } catch {
    // Keep the raw value if it isn't valid percent-encoding.
  }

  return isValidReferralCode(decoded) ? decoded : null;
};

/** Build a shareable referral link for the given code. */
export const buildReferralUrl = (code: string): string =>
  `${REFERRAL_SITE_ORIGIN}/?${REFERRAL_QUERY_PARAM}=${encodeURIComponent(code)}`;
